import { ChevronRightIcon } from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';

import { Button, Text } from '@/design-system';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { WaterfallZoomProvider } from '../store/WaterfallZoomContext';
import { useRunsInTreeStructure } from '../utils/useRunsInTreeStructure';
import { WaterfallZoomButtons } from './RunDetailsNavigation';
import { WaterfallLatencyGraph } from './WaterfallLatencyGraph';

interface ThreadWaterfallLatencyGraphProps {
  rootRuns: RunSchema[];
  selectedRun?: RunSchema;
  onNavigate: (run: RunSchema | null) => void;
  filteredRuns?: RunSchema[];
  foldedRuns: string[];
  setFoldedRuns: React.Dispatch<React.SetStateAction<string[]>>;
  unfilteredTree: ReturnType<typeof useRunsInTreeStructure>;
}

interface TurnWaterfallProps {
  rootRun: RunSchema;
  turnIndex: number;
  selectedRun?: RunSchema;
  onNavigate: (run: RunSchema | null) => void;
  filteredRuns?: RunSchema[];
  isExpanded: boolean;
  onToggle: () => void;
  foldedRuns: string[];
  setFoldedRuns: React.Dispatch<React.SetStateAction<string[]>>;
  treeState: ReturnType<typeof useRunsInTreeStructure>;
}

function TurnWaterfall({
  rootRun,
  turnIndex,
  selectedRun,
  onNavigate,
  filteredRuns,
  isExpanded,
  onToggle,
  setFoldedRuns,
  treeState,
}: TurnWaterfallProps) {
  const handleToggle = () => {
    // Toggle the expanded state
    onToggle();
    // Only select the root run when expanding (not collapsing)
    // This ensures children are loaded when the waterfall is shown
    if (!isExpanded) {
      onNavigate(rootRun);
    } else {
      onNavigate(null);
    }
  };

  return (
    <WaterfallZoomProvider>
      <div
        className={cn(
          'border-b border-secondary',
          turnIndex === 0 && 'border-t'
        )}
      >
        {/* Turn Header */}

        <div
          className={cn(
            'flex w-full items-center justify-between gap-2 px-4 py-2 ',
            selectedRun?.id === rootRun.id && 'bg-secondary'
          )}
        >
          <Button
            variant="plain"
            color="secondary"
            size="md"
            className="flex items-center gap-2 transition-colors hover:bg-secondary"
            onClick={handleToggle}
          >
            <ChevronRightIcon
              className={cn(
                'h-4 w-4 flex-shrink-0 text-tertiary transition-transform duration-200 ease-in-out',
                isExpanded && 'rotate-90'
              )}
            />
            <Text variant="sm" className="font-medium">
              Turn {turnIndex + 1}
            </Text>
            <Text variant="sm" className="text-tertiary">
              {rootRun.name}
            </Text>
          </Button>

          <span
            className={cn(
              'opacity-0 transition-opacity',
              isExpanded && 'opacity-100'
            )}
          >
            <WaterfallZoomButtons />
          </span>
        </div>

        {/* Waterfall Graph */}
        {isExpanded && (
          <div className="flex h-[400px] overflow-hidden border-t border-secondary bg-background">
            <WaterfallLatencyGraph
              treeState={treeState}
              activeRun={selectedRun}
              onNavigate={onNavigate}
              setFoldedRuns={setFoldedRuns}
              filteredRuns={filteredRuns}
            />
          </div>
        )}
      </div>
    </WaterfallZoomProvider>
  );
}

export function ThreadWaterfallLatencyGraph({
  rootRuns,
  selectedRun,
  onNavigate,
  filteredRuns,
  foldedRuns,
  setFoldedRuns,
  unfilteredTree,
}: ThreadWaterfallLatencyGraphProps) {
  const toggleTurn = (rootRunId: string) => {
    setFoldedRuns((prev) => {
      const turnChildren = unfilteredTree.childRunLookupWithFolds?.[rootRunId];
      if (prev.includes(rootRunId)) {
        return prev.filter((id) => id !== rootRunId);
      } else if (Array.isArray(turnChildren) && turnChildren.length > 0) {
        return [...prev, rootRunId];
      } else {
        return prev;
      }
    });
  };

  // for each root run, filter the flattened runs to only include the runs that are children of the root run
  // so it can be used in waterfall graph.
  // The tree state will contain more information than necessary but flattenedRuns is what's used.
  const rootRunsWithTreeState = useMemo(() => {
    return rootRuns.map((turn) => {
      const { flattenedRuns } = unfilteredTree.tree;
      return {
        ...turn,
        treeState: {
          ...unfilteredTree,
          tree: {
            ...unfilteredTree.tree,
            flattenedRuns: flattenedRuns.filter(
              (run) => run.rootRunId === turn.id
            ),
          },
        },
      };
    });
  }, [rootRuns, unfilteredTree]);

  if (rootRuns.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Text variant="sm" className="text-tertiary">
          No turns to display
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {rootRunsWithTreeState.map((turn, index) => {
        const turnChildren = unfilteredTree.childRunLookupWithFolds?.[turn.id];
        const hasChildren =
          Array.isArray(turnChildren) && turnChildren.length > 0;
        const isInFoldedRuns = foldedRuns.includes(turn.id);
        return (
          <TurnWaterfall
            key={turn.id}
            rootRun={turn}
            turnIndex={index}
            selectedRun={selectedRun}
            onNavigate={onNavigate}
            filteredRuns={filteredRuns}
            onToggle={() => toggleTurn(turn.id)}
            foldedRuns={foldedRuns}
            setFoldedRuns={setFoldedRuns}
            treeState={turn.treeState}
            isExpanded={!isInFoldedRuns && hasChildren}
          />
        );
      })}
    </div>
  );
}
